import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { RESOURCES, totalResources, turnKey, type Action, type GameView } from '../engine';
import { Board, type BoardTargets } from './board';
import { ChatPanel } from './chat';
import { autoMove, useAutoPlay, useGameSounds } from './effects';
import type { GameClient } from './game-client';
import { LogPanel, OptionsPanel, PlayersPanel, TurnClock, WinnerPanel } from './panels';
import { GameSummary } from './summary';
import { SeatColors } from './colors';
import { RESOURCE_COLORS, RESOURCE_LABELS, RESOURCE_TEXT, paletteColor } from './theme';
import { TradePanel } from './trade-panel';
import { TurnPanel, has, type BuildMode } from './turn-panel';
import { Die } from './widgets';

function statusText(view: GameView, names: readonly string[], you: number | null): string {
  const who = view.currentPlayer === you ? 'Your turn' : (names[view.currentPlayer] ?? '');
  const p = view.phase;
  switch (p.kind) {
    case 'setup':
      return p.step === 'settlement' ? `${who}: place a settlement` : `${who}: place a road next to it`;
    case 'preRoll':
      return `${who}: roll the dice`;
    case 'discard': {
      const owing = p.owed.flatMap((n, i) => (n > 0 ? [`${names[i]} (${n})`] : []));
      return `Discard half: ${owing.join(', ')}`;
    }
    case 'moveRobber':
      return `${who}: move the robber`;
    case 'steal':
      return `${who}: choose who to rob`;
    case 'main':
      return `${who}: build, trade, or end your turn`;
    case 'roadBuilding':
      return `${who}: place ${p.remaining} free road${p.remaining === 1 ? '' : 's'}`;
    case 'specialBuild':
      return `${who}: build between turns`;
    case 'gameOver':
      return p.winner === you ? 'You win!' : `${names[p.winner]} wins!`;
  }
}

export function GameScreen({ client, onExit }: { client: GameClient; onExit?: () => void }) {
  const { view, seat, legal, names, act } = client;
  const [mode, setMode] = useState<BuildMode>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setMode(null), [view.phase.kind, view.currentPlayer, view.turn]);
  const [info, setInfo] = useState<string | null>(null);
  // Whose pieces to spotlight: whatever's hovered (a player row or a piece), else the row tapped to pin it.
  const [hoverSeat, setHoverSeat] = useState<number | null>(null);
  const [pinnedSeat, setPinnedSeat] = useState<number | null>(null);
  const focusSeat = hoverSeat ?? pinnedSeat;
  useGameSounds(client);
  useEffect(() => {
    if (client.notice) setError(client.notice.text);
  }, [client.notice?.id]);
  useEffect(() => {
    if (!client.info) return;
    setInfo(client.info.text);
    const t = setTimeout(() => setInfo(null), 5000);
    return () => clearTimeout(t);
  }, [client.info?.id]);
  // Online, a successful action shows up as a new view; clear any stale error.
  useEffect(() => {
    if (!client.hotSeat) setError(null);
  }, [view]);

  const busy = !!client.pending;
  const run = (action: Action, as?: number) => {
    // Online, ignore taps while the last move is in flight; a double-tap would
    // otherwise come back as "not your turn".
    if (busy) return;
    const err = act(action, as);
    setError(err);
    if (!err && (action.type === 'buildRoad' || action.type === 'buildSettlement' || action.type === 'buildCity')) {
      setMode(null);
    }
  };

  // Online only: tell the victim what was taken (hot-seat shows it in the log).
  const prevView = useRef(view);
  useEffect(() => {
    const before = prevView.current;
    prevView.current = view;
    if (client.hotSeat || seat === null || before === view) return;
    const was = before.players[seat]?.resources;
    const now = view.players[seat]?.resources;
    const robbing = before.phase.kind === 'moveRobber' || before.phase.kind === 'steal';
    if (!was || !now || !robbing || view.currentPlayer === seat) return;
    const lost = RESOURCES.find((r) => now[r] === was[r] - 1 && totalResources(was) - totalResources(now) === 1);
    if (lost) setInfo(`${names[view.currentPlayer]} stole 1 ${RESOURCE_LABELS[lost].toLowerCase()} from you.`);
  }, [view]);

  const myMove = view.phase.kind !== 'gameOver' && (client.hotSeat ? false : legal.length > 0 && view.currentPlayer === seat);

  const [auto, setAuto] = useAutoPlay();
  const humanTurn = seat !== null && seat === view.currentPlayer && !client.cpu?.[seat];
  useEffect(() => {
    if (!humanTurn || busy) return;
    const move = autoMove(auto, legal, !!view.trade);
    if (!move) return;
    // A beat before acting so the player sees it happen (and can untick the box).
    const t = setTimeout(() => run(move), move.type === 'rollDice' ? 500 : 1200);
    return () => clearTimeout(t);
  }, [legal, auto.roll, auto.end, humanTurn, busy]);

  const kind = view.phase.kind;
  const targets = useMemo<BoardTargets>(() => {
    const vertices = new Set<number>();
    const edges = new Set<number>();
    const hexes = new Set<number>();
    for (const a of legal) {
      if (a.type === 'buildSettlement' && (kind === 'setup' || mode === 'settlement')) vertices.add(a.vertex);
      if (a.type === 'buildCity' && mode === 'city') vertices.add(a.vertex);
      if (a.type === 'buildRoad' && (kind === 'setup' || kind === 'roadBuilding' || mode === 'road')) edges.add(a.edge);
      if (a.type === 'moveRobber') hexes.add(a.hex);
    }
    return { vertices, edges, hexes };
  }, [legal, mode, kind]);

  // The board is memoised, so hand it callbacks that never change and read the latest render through a ref.
  const latest = useRef({ legal, mode, run });
  latest.current = { legal, mode, run };
  const handlers = useMemo(
    () => ({
      onVertex: (v: number) => {
        const { legal, mode, run } = latest.current;
        const city = legal.find((a) => a.type === 'buildCity' && a.vertex === v);
        run(city && mode === 'city' ? city : { type: 'buildSettlement', vertex: v });
      },
      onEdge: (e: number) => latest.current.run({ type: 'buildRoad', edge: e }),
      onHex: (h: number) => latest.current.run({ type: 'moveRobber', hex: h }),
    }),
    [],
  );
  const rolled = view.lastRoll ? view.lastRoll[0] + view.lastRoll[1] : null;
  const flash = useMemo(() => (rolled !== null && rolled !== 7 ? { token: rolled, key: view.turn } : null), [rolled, view.turn]);

  const colorKey = (client.colors ?? []).join(',');
  const seatColors = useMemo(() => names.map((_, i) => paletteColor(client.colors?.[i] ?? i)), [colorKey, names.length]);

  return (
    <SeatColors.Provider value={seatColors}>
    <div class={`game${busy ? ' busy' : ''}`} aria-busy={busy}>
      <div class="board-wrap">
        <div class={`status${myMove ? ' mine' : ''}`} aria-live="polite">
          <span data-testid="status">{statusText(view, names, client.hotSeat ? null : seat)}</span>
          <span class="turn-chip" data-testid="turn-count" title="Turns played so far">
            {view.turn === 0 ? 'Setup' : `Turn ${view.turn}`}
          </span>
          <TurnClock turn={turnKey(view)} seconds={kind === 'gameOver' ? null : client.turnSeconds} />
        </div>
        <Board
          board={view.board}
          robber={view.robber}
          buildings={view.buildings}
          roads={view.roads}
          targets={targets}
          {...handlers}
          flash={flash}
          focus={focusSeat}
          onFocusSeat={setHoverSeat}
        />
      </div>
      <div class="toasts">
        {error && (
          <div class="toast error" role="alert">
            <span>{error}</span>
            <button type="button" class="toast-close" aria-label="Dismiss" onClick={() => setError(null)}>
              ×
            </button>
          </div>
        )}
        {info && (
          <div class="toast info" role="status" data-testid="info">
            <span>{info}</span>
            <button type="button" class="toast-close" aria-label="Dismiss" onClick={() => setInfo(null)}>
              ×
            </button>
          </div>
        )}
      </div>
      <div class="side">
        <ActionBar client={client} run={run} status={statusText(view, names, client.hotSeat ? null : seat)} />
        {view.phase.kind === 'gameOver' && <WinnerPanel client={client} winner={view.phase.winner} onExit={onExit} />}
        {view.phase.kind === 'gameOver' && <GameSummary view={view} names={names} />}
        {seat !== null ? (
          <TurnPanel client={client} seat={seat} mode={mode} setMode={setMode} run={run} />
        ) : (
          <div class="panel" data-testid="spectating">
            <h2>Watching</h2>
            <p class="muted">You're watching this game. Hands are hidden.</p>
          </div>
        )}
        {view.phase.kind === 'steal' && seat === view.currentPlayer && (
          <div class="panel">
            <h2>Rob a player</h2>
            <div class="row wrap">
              {view.phase.candidates.map((c) => (
                <button key={c} data-testid={`steal-${c}`} onClick={() => run({ type: 'steal', victim: c })}>
                  {names[c]} ({view.players[c]!.resourceCount} cards)
                </button>
              ))}
            </div>
          </div>
        )}
        {seat !== null && <TradePanel client={client} seat={seat} run={run} />}
        <PlayersPanel
          view={view}
          names={names}
          connected={client.connected}
          cpu={client.cpu}
          bots={client.bots}
          focus={focusSeat}
          onHover={setHoverSeat}
          onPin={(i) => setPinnedSeat(pinnedSeat === i ? null : i)}
        />
        {client.chat && <ChatPanel {...client.chat} />}
        <LogPanel log={client.log} />
        <OptionsPanel client={client} auto={auto} setAuto={setAuto} onQuit={onExit && kind !== 'gameOver' ? onExit : undefined} />
      </div>
    </div>
    </SeatColors.Provider>
  );
}

/**
 * Dice and the turn's main button. A panel at the top of the sidebar on wide
 * screens; pinned to the bottom of the viewport on phones (see styles.css) so
 * it stays reachable while the board fills the screen.
 */
function ActionBar({ client, run, status }: { client: GameClient; run: (a: Action) => void; status: string }) {
  const { view, seat, legal, names } = client;
  const myTurn = seat !== null && seat === view.currentPlayer;
  const hand = seat !== null ? view.players[seat]?.resources : null;
  const roll = view.lastRoll;
  const seven = !!roll && roll[0] + roll[1] === 7;
  // During setup there's no roll yet; show the one that decided who goes first.
  const winningRound = view.phase.kind === 'setup' ? view.firstRoll?.[view.firstRoll.length - 1] : undefined;
  const opening = winningRound?.find((r) => r.player === (view.firstPlayer ?? 0));
  // Spectators keep the bar too: hiding it between rolls would make the page jump every turn.
  if (view.phase.kind === 'gameOver') return null;
  return (
    <div class="panel action-bar" data-testid="action-bar">
      <span class="bar-status">{status}</span>
      <div class="bar-row">
        {roll ? (
          <span
            class={`dice${seven ? ' seven' : ''}`}
            data-testid="dice"
            key={`${view.turn}-${roll.join('')}`}
            title={seven ? 'Rolled 7: robber!' : `Rolled ${roll[0] + roll[1]}`}
          >
            <Die value={roll[0]} />
            <Die value={roll[1]} />
            <span class="dice-total">{roll[0] + roll[1]}</span>
          </span>
        ) : opening ? (
          <span class="dice first-roll" data-testid="first-roll" title={`Rolled for who goes first: ${names[opening.player]} won`}>
            <Die value={opening.dice[0]} />
            <Die value={opening.dice[1]} />
            <span class="dice-total">{names[opening.player]} first</span>
          </span>
        ) : (
          <span class="dice-placeholder" />
        )}
        {hand && (
          <span class="mini-hand" aria-label="Your hand">
            {RESOURCES.map((r) => (
              <span key={r} class="mini-chip" style={{ background: RESOURCE_COLORS[r], color: RESOURCE_TEXT[r] }} title={RESOURCE_LABELS[r]}>
                {hand[r]}
              </span>
            ))}
          </span>
        )}
        <span class="bar-action">
          {myTurn && has(legal, 'rollDice') && (
            <button class="primary" data-testid="roll" onClick={() => run({ type: 'rollDice' })}>
              Roll dice
            </button>
          )}
          {myTurn && has(legal, 'endTurn') && (
            <button class="primary" data-testid="end-turn" onClick={() => run({ type: 'endTurn' })}>
              {view.phase.kind === 'specialBuild' ? 'Done building' : 'End turn'}
            </button>
          )}
        </span>
      </div>
    </div>
  );
}
